import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useEyeconxStore } from "../../../../../../store/eyeconxStore";
import bookingService from "../../../../../../services/booking.service";
import Loading from "../../../../../common/Loading";

const ManageAppointmentType = ({ onBack }) => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const [appointmentTypes, setAppointmentTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [addingNew, setAddingNew] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm();

    const getAppointmentTypes = async () => {
        setLoading(true);
        try {
            const res = await bookingService.getAllAppointmentTypes(clinicId);
            setAppointmentTypes(res.data);
        } catch (error) {
            console.log('Error fetching appointment types:', error);
        } finally {
            setLoading(false);
        }
    };

    const addAppointmentType = async (data) => {
        try {
            const appointmentTypeData = {
                apptType: data.apptType,
                timeSlot: parseInt(data.timeSlot),
                isActive: data.status,
                clinicId: clinicId,
            };
            const res = await bookingService.addUpdateAppointmentType(clinicId, appointmentTypeData);
            setAppointmentTypes((prev) => [...prev, res.data]);
            setAddingNew(false);
            reset();
        } catch (error) {
            console.log('Error adding appointment type:', error);
        }
    };

    const updateAppointmentType = async (apptTypeId, data) => {
        try {
            const updatedAppointmentTypeData = {
                id: apptTypeId,
                apptType: data.apptType,
                timeSlot: parseInt(data.timeSlot),
                isActive: data.status,
                clinicId: clinicId,
            };
            const res = await bookingService.addUpdateAppointmentType(clinicId, updatedAppointmentTypeData);
            setAppointmentTypes((prev) =>
                prev.map((type) =>
                    type.id === apptTypeId
                        ? { ...type, ...res.data, apptType: data.apptType, timeSlot: parseInt(data.timeSlot), isActive: data.status }
                        : type
                )
            );
            setEditingId(null);
        } catch (error) {
            console.log('Error updating appointment type:', error);
        }
    };

    const deleteAppointmentType = async (apptTypeId) => {
        try {
            await bookingService.deleteAppointmentTypeById(clinicId, apptTypeId);
            setAppointmentTypes((prev) => prev.filter((type) => type.id !== apptTypeId));
            setDeleteConfirm(null);
        } catch (error) {
            console.log('Error deleting appointment type:', error);
        }
    };

    const handleEdit = (appointmentType) => {
        setEditingId(appointmentType.id);
        setValue('apptType', appointmentType.apptType);
        setValue('timeSlot', appointmentType.timeSlot.toString());
        setValue('status', appointmentType.isActive);
    };

    const handleCancel = () => {
        setEditingId(null);
        setAddingNew(false);
        reset();
    };

    const handleAddNew = () => {
        setAddingNew(true);
        setEditingId(null);
        reset({ apptType: '', timeSlot: '30', status: true });
    };

    useEffect(() => {
        getAppointmentTypes();
    }, []);

    if (loading) {
        return <Loading />;
    }

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                <div>
                    <h6 className="mb-0">
                        Manage Appointment Types
                    </h6>
                    <p className="text-muted mb-0">Manage your appointment types and durations</p>
                </div>
                <div className="btn-group">
                    {!addingNew && (
                        <button className="btn btn-outline-primary" onClick={handleAddNew}>
                            <i className="bi bi-plus-circle me-2"></i> Add New Type
                        </button>
                    )}
                    <button className="btn btn-outline-secondary" onClick={onBack}>
                        <i className="bi bi-arrow-left me-2"></i> Back
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-2">Loading appointment types...</p>
                </div>
            ) : appointmentTypes.length === 0 && !addingNew ? (
                <div className="text-center py-5">
                    <i className="bi bi-calendar-x display-1 text-muted"></i>
                    <h5 className="text-muted mt-3">No appointment types found</h5>
                    <p className="text-muted">Get started by adding your first appointment type</p>
                    <button className="btn btn-primary mt-2" onClick={handleAddNew}>
                        <i className="bi bi-plus-circle me-2"></i> Add First Type
                    </button>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover align-middle table-striped">
                        <thead>
                        <tr>
                            <th scope="col">Status</th>
                            <th scope="col" className="ps-4"><i className="bi bi-calendar-text me-2"></i> Appointment Type</th>
                            <th scope="col"><i className="bi bi-clock me-2"></i> Duration (mins)</th>

                            <th scope="col">Last Modified</th>
                            <th scope="col" className="text-end pe-4">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {/* Add New Row */}
                        {addingNew && (
                            <tr className="border-top bg-light position-relative">
                                <td>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            {...register('status')}
                                            defaultChecked
                                        />
                                    </div>
                                </td>
                                <td className="ps-4">
                                    <div className="flex-grow-1">
                                        <input
                                            type="text"
                                            className={`form-control ${errors.apptType ? 'is-invalid' : ''}`}
                                            placeholder="Enter appointment type..."
                                            {...register('apptType', {
                                                required: 'Appointment type is required',
                                                minLength: { value: 2, message: 'Appointment type must be at least 2 characters' },
                                            })}
                                        />
                                        {errors.apptType && <div className="invalid-feedback">{errors.apptType.message}</div>}
                                    </div>
                                </td>
                                <td>
                                    <select
                                        className={`form-select ${errors.timeSlot ? 'is-invalid' : ''}`}
                                        {...register('timeSlot', {
                                            required: 'Time slot is required',
                                            validate: value => value > 0 || 'Time slot must be greater than 0'
                                        })}
                                    >
                                        <option value="15">15 minutes</option>
                                        <option value="20">20 minutes</option>
                                        <option value="30">30 minutes</option>
                                        <option value="45">45 minutes</option>
                                        <option value="60">60 minutes</option>
                                    </select>
                                    {errors.timeSlot && <div className="invalid-feedback">{errors.timeSlot.message}</div>}
                                </td>
                                <td><small className="text-muted">-</small></td>
                                <td><small className="text-muted">-</small></td>
                                <td className="text-end pe-4">
                                    <div className="btn-group">
                                        <button className="btn btn-success" onClick={handleSubmit(addAppointmentType)}>
                                            <i className="bi bi-check"></i>
                                        </button>
                                        <button className="btn btn-outline-secondary" onClick={handleCancel}>
                                            <i className="bi bi-x"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {/* Existing Appointment Types */}
                        {appointmentTypes.map((type) => (
                            <tr key={type.id} className="border-top position-relative">
                                {/* Delete Confirmation Overlay */}
                                {deleteConfirm?.id === type.id && (
                                    <td colSpan="6" className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-90 d-flex justify-content-between align-items-center px-4" style={{ zIndex: 10 }}>
                                        <div className="d-flex align-items-center">
                                            <i className="bi bi-exclamation-triangle-fill text-danger me-2 fs-4"></i>
                                            <strong>Confirm delete "{type.apptType}"?</strong>
                                        </div>
                                        <div className="btn-group">
                                            <button className="btn btn-danger text-white" onClick={() => deleteAppointmentType(type.id)}>
                                                <i className="bi bi-trash me-1"></i> Confirm
                                            </button>
                                            <button className="btn btn-outline-secondary" onClick={() => setDeleteConfirm(null)}>
                                                Cancel
                                            </button>
                                        </div>
                                    </td>
                                )}

                                <td>
                                    {editingId === type.id ? (
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                {...register('status')}
                                                defaultChecked={type.isActive}
                                            />
                                        </div>
                                    ) : (
                                        <span className={`badge ${type.isActive ? 'bg-success' : 'bg-secondary'} ${type.isActive ? 'text-white' : 'text-white'} fs-6`}>
                                                <i className={`bi ${type.isActive ? 'bi-check-circle' : 'bi-pause-circle'} me-1`}></i>
                                            {type.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                    )}
                                </td>
                                <td className="ps-4">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            {editingId === type.id ? (
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.apptType ? 'is-invalid' : ''}`}
                                                    defaultValue={type.apptType}
                                                    {...register('apptType', {
                                                        required: 'Appointment type is required',
                                                        minLength: { value: 2, message: 'Appointment type must be at least 2 characters' },
                                                    })}
                                                />
                                            ) : (
                                                <>
                                                    <h6 className="mb-0 fw-semibold">{type.apptType}</h6>
                                                    <small className="text-muted">ID: {type.id.slice(0, 8)}...</small>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    {editingId === type.id ? (
                                        <select
                                            className={`form-select ${errors.timeSlot ? 'is-invalid' : ''}`}
                                            defaultValue={type.timeSlot.toString()}
                                            {...register('timeSlot', {
                                                required: 'Time slot is required',
                                                validate: value => value > 0 || 'Time slot must be greater than 0'
                                            })}
                                        >
                                            <option value="15">15 minutes</option>
                                            <option value="20">20 minutes</option>
                                            <option value="30">30 minutes</option>
                                            <option value="45">45 minutes</option>
                                            <option value="60">60 minutes</option>
                                        </select>
                                    ) : (
                                        <span className="badge bg-info text-white">
                                                <i className="bi bi-clock me-1"></i>
                                            {type.timeSlot} mins
                                            </span>
                                    )}
                                </td>

                                <td><small className="text-muted">{type.modifiedAt}</small></td>
                                <td className="text-end pe-4">
                                    <div className="btn-group">
                                        {editingId === type.id ? (
                                            <>
                                                <button className="btn btn-success" onClick={handleSubmit((data) => updateAppointmentType(type.id, data))}>
                                                    <i className="bi bi-check"></i>
                                                </button>
                                                <button className="btn btn-outline-secondary" onClick={handleCancel}>
                                                    <i className="bi bi-x"></i>
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button className="btn btn-outline-info" onClick={() => handleEdit(type)}>
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                                <button className="btn btn-outline-danger" onClick={() => setDeleteConfirm(type)}>
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

export default ManageAppointmentType;